import { CustomDatePicker } from '@components/controls/CustomDatePicker';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useGetMacroplanogramResults from '@store/hooks/useGetMacroplanogramResults';
import { AgileMacroplanogram } from '@components/agile/types/AgileMacroplanogramTypes';
import AgileURLParameterTypes from '@components/agile/types/AgileURLParameterTypes';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Alert from '@amzn/meridian/alert';
import StoreSelector from 'src/components/selectors/ProgramStoreSelector';
import NavigationHelper from '@components/agile/utils/URLParametersUtils';

export interface AtAgileHeaderProps {
  setZoneDetailsAndTime: (
    zoneDetails: AgileMacroplanogram | undefined,
    swapTime: number,
    macroplanogramList: AgileMacroplanogram[] | undefined,
  ) => void;
  urlParams: AgileURLParameterTypes;
  setUrlParameter: (urlParams: AgileURLParameterTypes) => void;
  setRecommendationFlag?: (RecommendationFlag: boolean) => void;
  enableZoneListInHeader: boolean;
}

const AtAgileHeader = (props: AtAgileHeaderProps) => {
  const { setZoneDetailsAndTime, urlParams, setUrlParameter, setRecommendationFlag, enableZoneListInHeader } = props;
  const [searchParams, setSearchParams] = useSearchParams();
  const [store, setStore] = useState(urlParams?.storeId);
  const [date, setDate] = useState(urlParams?.date);
  const [zoneId, setZoneId] = useState(urlParams?.zoneId);
  const [zoneSelectionFlag, setZoneSelectionFlag] = useState(false);
  const timeStamp = new Date(date).getTime();
  const { macroplanogramList, zoneNameList, isSuccess } = useGetMacroplanogramResults(
    {
      storeId: store,
      timestamp: timeStamp,
      includeFuture: false,
      includeDraft: false,
    },
    { enabled: !!store && !!timeStamp },
  );
  const zoneSelectionValidation = (zone: AgileMacroplanogram | undefined) => {
    if (zone === undefined || zone === null) {
      setZoneSelectionFlag(true);
    } else {
      setZoneSelectionFlag(false);
    }
  };
  const filteredZoneList = zoneNameList || [];

  const handleZoneChange = (newZoneId: string) => {
    setZoneId(newZoneId);
    setZoneSelectionFlag(newZoneId === '');
  };

  useEffect(() => {
    setSearchParams(
      NavigationHelper.createSearchParams(
        {
          storeId: store,
          date,
          zoneId,
        },
        urlParams.demo,
      ),
    );
    setUrlParameter({
      storeId: store,
      date,
      zoneId,
      demo: urlParams.demo,
    });
    console.log('searchParams:', searchParams);
    if (zoneId !== undefined && isSuccess) {
      const filteredMacroplanogramList = macroplanogramList ?? [];

      // Find zone by matching subcategory IDs in zoneId
      const zoneDetails: AgileMacroplanogram | undefined = filteredMacroplanogramList?.find((obj) => {
        // First try exact entryId match (works for both old and new models)
        if (obj.entryId === zoneId) {
          return true;
        }

        // For aggregate model: check if zoneId is composite subcategory IDs
        if (zoneId.includes('|')) {
          const selectedSubcategoryIds = zoneId.split('|').sort();
          const macroSubcategoryIds = (obj.storeSubcategories?.map((sc) => sc.subcategoryId) || []).sort();

          // Check if all selected subcategories exist in this macroplanogram
          return selectedSubcategoryIds.every((scId) => macroSubcategoryIds.includes(scId));
        }

        // Single subcategory ID
        return obj.storeSubcategories?.some((sc) => sc.subcategoryId === zoneId);
      });
      let filteredZoneDetails = zoneDetails;
      // Filter zoneDetails to only include selected zone's subcategories
      if (zoneDetails && zoneId !== zoneDetails.entryId) {
        // This is aggregate model - filter subcategories
        const selectedSubcategoryIds = zoneId.includes('|') ? zoneId.split('|') : [zoneId];

        filteredZoneDetails = {
          ...zoneDetails,
          storeSubcategories: zoneDetails.storeSubcategories?.filter((sc) =>
            selectedSubcategoryIds.includes(sc.subcategoryId),
          ),
        };
      }

      setZoneDetailsAndTime(filteredZoneDetails, timeStamp, filteredMacroplanogramList ?? []);
      zoneSelectionValidation(filteredZoneDetails);
    }
    if (setRecommendationFlag) {
      setRecommendationFlag(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, macroplanogramList, store, timeStamp, zoneId, urlParams.demo]);

  return (
    <div>
      <Row alignmentVertical="bottom">
        <StoreSelector selectedStoreId={store} onStoreSelect={setStore} />
        <CustomDatePicker value={date} onChange={setDate} label="Date" />
        {enableZoneListInHeader && (
          <Select
            label="UPHL2"
            value={zoneId}
            onChange={handleZoneChange}
            placeholder="Select Zone..."
            width={300}
            error={zoneSelectionFlag}
          >
            {filteredZoneList &&
              store &&
              timeStamp &&
              filteredZoneList.map((zoneName) => <SelectOption value={zoneName.zoneId} label={zoneName.zoneName} />)}
            <SelectOption value="" label="None" />
          </Select>
        )}
        {zoneSelectionFlag && enableZoneListInHeader && (
          <Alert size="small" type="error">
            Please select a zone
          </Alert>
        )}
      </Row>
    </div>
  );
};
export default AtAgileHeader;
